import React, { useEffect, useState, useMemo, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import usePulidoLink from "../../hooks/usePulidoLink";
import { formatPrice } from "../../utils";
import ExpandableTable from "../../components/design-system/ExpandableTable";
import ConfirmModal from "../../components/design-system/ConfirmModal";
import DocumentAttacher from "../../components/design-system/DocumentAttacher";
import PedirCotizacionMaterial from "./PedirCotizacionMaterial";
import PieceLookup from "../../components/design-system/PieceLookup";
import SaludPrecios from "./SaludPrecios";
import PulsoVentas from "./PulsoVentas";

// ─── constantes ──────────────────────────────────────────────────────────────

// F5.8 — Lifecycle: cotizada → aprobada → terminada.
// Ago-27 (funnel ventas): + enviada / seguimiento / ganada / perdida / vencida.
// El select de edición solo ofrece los destinos que acepta el PATCH genérico;
// ganada/perdida/vencida se manejan por sus flujos propios (aprobar, resultado,
// script de vencidas).
const STATUS_OPTIONS = [
  { value: "cotizada",    label: "Cotizada"    },
  { value: "enviada",     label: "Enviada"     },
  { value: "seguimiento", label: "Seguimiento" },
  { value: "aprobada",    label: "Aprobada"    },
  { value: "terminada",   label: "Terminada"   },
];

// Colores del funnel (badges + chips). enviada azul · seguimiento naranjo ·
// ganada verde · perdida rojo · vencida gris (pedido explícito ago-27).
const STATUS_META = {
  cotizada:    { label: "Cotizada",    color: "#a78bfa", bg: "#1f1635", border: "#2d2050" },
  enviada:     { label: "Enviada",     color: "#3498db", bg: "#0f2230", border: "#1d4060" },
  seguimiento: { label: "Seguimiento", color: "#f39c12", bg: "#2a1d08", border: "#4a3410" },
  ganada:      { label: "Ganada",      color: "#2ecc71", bg: "#0d2a15", border: "#0d4020" },
  aprobada:    { label: "Aprobada",    color: "#2ecc71", bg: "#0d2a15", border: "#0d4020" },
  perdida:     { label: "Perdida",     color: "#e74c3c", bg: "#2a0d0d", border: "#4a1a1a" },
  vencida:     { label: "Vencida",     color: "#7f8c8d", bg: "#1e1e1e", border: "#333"    },
  terminada:   { label: "Terminada",   color: "#27ae60", bg: "#0d2a15", border: "#0d4020" },
  // legacy
  open:     { label: "Cotizada", color: "#a78bfa", bg: "#1f1635", border: "#2d2050" },
  sent:     { label: "Enviada",  color: "#3498db", bg: "#0f2230", border: "#1d4060" },
  revision: { label: "Cotizada", color: "#a78bfa", bg: "#1f1635", border: "#2d2050" },
  approved: { label: "Aprobada", color: "#2ecc71", bg: "#0d2a15", border: "#0d4020" },
  rejected: { label: "Perdida",  color: "#e74c3c", bg: "#2a0d0d", border: "#4a1a1a" },
};

const QBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.cotizada;
  return (
    <span style={{
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700,
      whiteSpace: "nowrap", letterSpacing: ".03em",
    }}>
      {m.label}
    </span>
  );
};

// Paquete A — tipos de trabajo por línea (badge por tipo predominante + filtro)
const TIPO_META = {
  ref: { label: "⚙️ REF", color: "#3498db", bg: "#0f2230", border: "#1d4060", title: "Piezas de catálogo" },
  fab: { label: "🔨 FAB", color: "#a78bfa", bg: "#1f1635", border: "#2d2050", title: "Fabricación a pedido" },
  rep: { label: "🔧 REP", color: "#f39c12", bg: "#2a1d08", border: "#4a3410", title: "Reparación" },
};

const TipoBadge = ({ tipo }) => {
  const m = TIPO_META[tipo] || TIPO_META.ref;
  return (
    <span title={m.title} style={{
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      borderRadius: 4, padding: "1px 6px", fontSize: 9, fontWeight: 700,
      whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};

// Tipo predominante de una cotización = el más frecuente entre sus líneas
// (empate: fab > rep > ref, para que lo especial no se esconda).
const tipoPredominante = q => {
  const count = { ref: 0, fab: 0, rep: 0 };
  (q.items || []).forEach(it => { count[it.tipoTrabajo || "ref"] += 1; });
  return ["fab", "rep", "ref"].reduce((best, t) =>
    count[t] > count[best] ? t : best, "ref");
};

const MOTIVOS_PERDIDA = [
  { value: "precio",        label: "Precio" },
  { value: "plazo",         label: "Plazo" },
  { value: "competencia",   label: "Se lo llevó la competencia" },
  { value: "sin_respuesta", label: "Cliente no respondió" },
  { value: "otro",          label: "Otro" },
];

const CLOSING_DOC_TYPES = [
  { value: "factura",      label: "Factura"          },
  { value: "guia",         label: "Guía de despacho" },
  { value: "boleta",       label: "Boleta"           },
  { value: "nota_credito", label: "Nota de crédito"  },
];

const PAYMENT_TERMS = [
  "contado", "15 días", "30 días", "45 días", "60 días",
  "Contado contra entrega", "50% con la OC · 50% contra entrega", "Pago anticipado",
];

// Opciones rápidas de plazo de entrega (26-ago, requerimiento Nico: el plazo
// estaba fijo "5 a 6 días hábiles" y no se podía cambiar). Vive en
// quotation.condiciones y sale impreso en el PDF.
const PLAZOS_ENTREGA = ["5 a 6 días hábiles", "10 días hábiles", "15 días hábiles", "Según programa acordado"];

const S = {
  wrap: {
    // clamp: 22px lateral en desktop, ~10px en móvil (360-430px)
    display: "block", padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
    minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
    fontSize: 13, textAlign: "left", boxSizing: "border-box", width: "100%",
  },
  sectionTitle: {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".07em",
    margin: "14px 0 8px",
  },
};

const fmt  = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short" }) : "—";
const fmtN = n => Number(n || 0).toLocaleString("es-CL");
const fmtMoney = v => v != null ? `$${formatPrice(v)}` : "—";

// Normaliza ítems al formato { sku, desc, qty, pUnit, source, pieceId }.
// Prioridad: datos del catálogo (piece populado) > campo crudo de importación > nuevo campo normalizado.
const normQItems = (items = []) =>
  items.map(it => ({
    sku:     it.piece?.sku   || it.sku     || "",
    desc:    it.piece?.name  || it.name    || it.desc    || "",
    qty:     it.qty          || it.quantity || 1,
    pUnit:   it.piece?.price || it.unitPrice || it.pUnit || 0,
    source:  it.source       || (it.piece ? "catalog" : null),
    pieceId: it.piece?._id   || it.pieceId || null,
    // Paquete A (ago-27) — clasificación por línea. Estos campos viajan de
    // vuelta al PATCH tal cual (el server preserva el codigoTrabajo existente).
    tipoTrabajo:        it.tipoTrabajo || "ref",
    codigoTrabajo:      it.codigoTrabajo || null,
    componenteReparado: it.componenteReparado || null,
    planos:             it.planos || [],
  }));

// ─── componente principal ─────────────────────────────────────────────────────

const Quotations = () => {
  useTitle("Cotizaciones");
  const navigate = useNavigate();
  const location = useLocation();

  const PER_PAGE = 50;
  const [page, setPage] = useState(1);
  const [quotations, setQuotations] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [clients,    setClients]    = useState([]);

  const [deleteTarget,   setDeleteTarget]   = useState(null);
  const [convertTarget,  setConvertTarget]  = useState(null);  // F5.8 — modal aprobar
  const [closeTarget,    setCloseTarget]    = useState(null);  // F5.8 — modal cerrar
  const [attachOCTarget, setAttachOCTarget] = useState(null);
  const [ocInput,        setOcInput]        = useState("");
  // T2e (ago-26) — checklist de arranque al aprobar: plazo comprometido + material
  const [deadlineInput,  setDeadlineInput]  = useState("");
  const [materialInput,  setMaterialInput]  = useState("");

  // F5.8 — input para cerrar cotización (factura/guía/etc.)
  const [closeDocType,   setCloseDocType]   = useState("factura");
  const [closeDocNumber, setCloseDocNumber] = useState("");

  // 💰 Salud de precios (28-ago): panel colapsable, cerrado por defecto.
  // Solo se monta al abrirlo (el fetch a /sii/salud-precios no corre de gratis).
  // El endpoint acepta admin/accountant/seller — a un operator el botón solo
  // le mostraba un 403, así que para ese rol no se ofrece.
  const auth = useContext(AuthContext);
  const puedeVerSalud = auth?.can?.("ver_pulso_ventas") || false;
  // 28-ago (roles): /pulido es solo-admin — los links "sin precio" de no-admin
  // van al catálogo (/pieces) en su lugar.
  const puedeVerPulido = auth?.can?.("ver_pulido") || false;
  const [showSalud, setShowSalud] = useState(false);

  // filtros (tarjetas clicleables + chips) + búsqueda
  const [activeFilter, setActiveFilter] = useState("all");
  const [filterTipo,   setFilterTipo]   = useState("all"); // ref/fab/rep (Paquete A)
  const [searchQuery,  setSearchQuery]  = useState("");

  // ── Paquete B (ago-27): funnel + seguimiento ──
  const [pendientes, setPendientes] = useState([]);         // GET /seguimiento/pendientes
  const [showSeguir, setShowSeguir] = useState(false);      // 01-sep (Maxi): lista plegada, presente por el contador
  const [segTarget,  setSegTarget]  = useState(null);       // modal "Seguimiento hecho"
  const [segNota,    setSegNota]    = useState("");
  const [resTarget,  setResTarget]  = useState(null);       // modal "Registrar resultado"
  const [resMotivo,  setResMotivo]  = useState("precio");
  const [resDetalle, setResDetalle] = useState("");

  // "Ver PDF" — genera el PDF en el servidor (mismo documento que manda Hermes)
  // y lo abre en una pestaña nueva para verlo/guardarlo/reenviarlo.
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const handleVerPdf = async (data) => {
    try {
      setPdfLoadingId(data._id);
      const res = await backend.get(`quotation/${data._id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Error generando PDF de cotización:", err);
      window.alert("No se pudo generar el PDF de la cotización. Reintente en unos segundos.");
    } finally {
      setPdfLoadingId(null);
    }
  };

  // ── data ──────────────────────────────────────────────────────────────────

  const load = async () => {
    setLoading(true);
    try {
      const [qRes, clRes, pendRes] = await Promise.all([
        backend.get("quotation"),
        backend.get("entity").catch(() => backend.get("entity/client").catch(() => ({ data: [] }))),
        backend.get("quotation/seguimiento/pendientes").catch(() => ({ data: [] })),
      ]);
      setQuotations(
        (qRes.data || []).map(q => ({ ...q, items: normQItems(q.items) }))
      );
      setClients(clRes.data || []);
      setPendientes(pendRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const reloadPendientes = async () => {
    try {
      const res = await backend.get("quotation/seguimiento/pendientes");
      setPendientes(res.data || []);
    } catch (e) { /* panel queda como estaba */ }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.new) {
      const q = location.state.new;
      setQuotations(p => [{ ...q, items: normQItems(q.items) }, ...p]);
      navigate("", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // ── métricas ──────────────────────────────────────────────────────────────

  // Estados: lifecycle F5.8 + funnel ago-27. Aliases legacy mapeados:
  // open/sent/revision → cotizada/enviada; approved → ganada; rejected → perdida.
  const isCotizada    = q => ["cotizada", "open", "revision"].includes(q.status);
  const isEnviada     = q => q.status === "enviada" || q.status === "sent";
  const isSeguimiento = q => q.status === "seguimiento";
  // "ganada" incluye "aprobada"/"approved" (docs pre-funnel): mismo significado.
  const isGanada      = q => ["ganada", "aprobada", "approved"].includes(q.status);
  const isPerdida     = q => q.status === "perdida" || q.status === "rejected";
  const isVencida     = q => q.status === "vencida";
  const isTerminada   = q => q.status === "terminada";
  // Comercialmente activa = todavía se puede gestionar/aprobar
  const isActivaComercial = q => isCotizada(q) || isEnviada(q) || isSeguimiento(q) || isVencida(q);
  // legacy compat para el detalle (bloqueo de ítems)
  const isAprobada = isGanada;

  const total = quotations.length;

  // ── KPIs (ago-27, tarjetas clicables estilo Pagos) ──
  const hoy = new Date();
  const mesActual  = `${hoy.getFullYear()}-${hoy.getMonth()}`;
  const anioActual = hoy.getFullYear();
  const enMes  = d => d && `${new Date(d).getFullYear()}-${new Date(d).getMonth()}` === mesActual;
  const enAnio = d => d && new Date(d).getFullYear() === anioActual;
  const fechaGanada = q => q.resultado?.fecha || q.updatedAt;

  const cotizadoMes = quotations.filter(q => enMes(q.createdAt));
  const ganadasMes  = quotations.filter(q => (isGanada(q) || isTerminada(q)) && enMes(fechaGanada(q)));
  // 01-sep (Maxi): los KPIs comerciales hablan en NETO, con el IVA aparte.
  const netoDe = q => Number(q.subtotal) || Math.round((Number(q.total) || 0) / 1.19);
  const ivaDe  = q => Number(q.iva) != null && Number(q.iva) > 0 ? Number(q.iva) : (Number(q.total) || 0) - netoDe(q);
  const cotizadoMesMonto = cotizadoMes.reduce((s, q) => s + netoDe(q), 0);
  const cotizadoMesIva   = cotizadoMes.reduce((s, q) => s + ivaDe(q), 0);
  const ganadoMesMonto   = ganadasMes.reduce((s, q) => s + netoDe(q), 0);
  const ganadoMesIva     = ganadasMes.reduce((s, q) => s + ivaDe(q), 0);
  // % conversión del año: ganadas / enviadas. "Enviada" = tiene enviadaAt o ya
  // avanzó más allá de cotizada (las viejas sin enviadaAt no quedan fuera).
  const fueEnviada = q => !!q.enviadaAt || !isCotizada(q);
  const enviadasAnio = quotations.filter(q => enAnio(q.createdAt) && fueEnviada(q)).length;
  // Ganadas por la fecha en que se GANARON (resultado.fecha), no por creación
  // (auditoría Y5): una cotización creada en dic y ganada en ene cuenta en enero.
  const ganadasAnio  = quotations.filter(q => (isGanada(q) || isTerminada(q)) && enAnio(q.resultado?.fecha || q.createdAt)).length;
  const conversion = enviadasAnio > 0 ? Math.round((ganadasAnio / enviadasAnio) * 100) : 0;
  const idsPendientes = useMemo(() => new Set(pendientes.map(p => String(p._id))), [pendientes]);

  const STATUS_CHIP_FILTERS = [
    { key: "all",         label: "Todas",       test: () => true },
    { key: "cotizadas",   label: "Cotizadas",   test: isCotizada },
    { key: "enviadas",    label: "Enviadas",    test: isEnviada },
    { key: "seguimiento", label: "Seguimiento", test: isSeguimiento },
    { key: "ganadas",     label: "Ganadas",     test: isGanada },
    { key: "perdidas",    label: "Perdidas",    test: isPerdida },
    { key: "vencidas",    label: "Vencidas",    test: isVencida },
    { key: "terminadas",  label: "Terminadas",  test: isTerminada },
  ];

  const filteredQuotations = useMemo(() => quotations.filter(q => {
    // tarjetas KPI
    if (activeFilter === "cotizado_mes") { if (!enMes(q.createdAt)) return false; }
    else if (activeFilter === "ganado_mes") { if (!((isGanada(q) || isTerminada(q)) && enMes(fechaGanada(q)))) return false; }
    else if (activeFilter === "por_seguir") { if (!idsPendientes.has(String(q._id))) return false; }
    else {
      // chips de status
      const chip = STATUS_CHIP_FILTERS.find(c => c.key === activeFilter);
      if (chip && !chip.test(q)) return false;
    }

    // chips de tipo (Paquete A)
    if (filterTipo !== "all" && tipoPredominante(q) !== filterTipo) return false;

    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      String(q.number || "").includes(query) ||
      String(q.workOrderNumber || "").includes(query) ||
      String(q.ocNumber || "").toLowerCase().includes(query) ||
      (q.client?.name || "").toLowerCase().includes(query) ||
      (q.description  || "").toLowerCase().includes(query) ||
      (q.items || []).some(it =>
        (it.sku  || "").toLowerCase().includes(query) ||
        (it.desc || "").toLowerCase().includes(query) ||
        (it.codigoTrabajo || "").toLowerCase().includes(query)
      )
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [quotations, activeFilter, filterTipo, searchQuery, idsPendientes]);
  const isFiltered = activeFilter !== "all" || filterTipo !== "all" || !!searchQuery.trim();

  // Paginación 50 por página (igual a /trabajos)
  const totalPages = Math.ceil(filteredQuotations.length / PER_PAGE) || 1;
  const pageRows = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filteredQuotations.slice(start, start + PER_PAGE);
  }, [filteredQuotations, page]);
  useEffect(() => { setPage(1); }, [activeFilter, filterTipo, searchQuery]);

  // ── handlers ──────────────────────────────────────────────────────────────

  // ── Paquete B (ago-27): acciones del funnel ──

  // "Marcar enviada": cotizada → enviada (arranca el reloj de 48h)
  const marcarEnviada = async (q) => {
    try {
      const res = await backend.patch(`quotation/${q._id}/estado`, { status: "enviada" });
      const upd = res.data?.quotation;
      setQuotations(prev => prev.map(x =>
        x._id === q._id ? { ...x, status: "enviada", enviadaAt: upd?.enviadaAt || new Date().toISOString() } : x
      ));
    } catch (e) {
      alert("Error al marcar enviada: " + (e?.response?.data?.error || e.message));
    }
  };

  // "✓ Seguimiento hecho": registra la nota y resetea el reloj
  const handleSeguimiento = async () => {
    if (!segTarget?._id) return;
    if (!segNota.trim()) { alert("Escribe qué gestión hiciste (llamada, correo, visita…)."); return; }
    try {
      const res = await backend.post(`quotation/${segTarget._id}/seguimiento`, { nota: segNota.trim() });
      const upd = res?.data?.quotation;
      setQuotations(prev => prev.map(x =>
        x._id === segTarget._id
          ? { ...x, status: upd?.status || x.status, seguimientos: upd?.seguimientos || x.seguimientos }
          : x
      ));
      setSegTarget(null);
      setSegNota("");
      reloadPendientes();
    } catch (e) {
      alert("Error al registrar seguimiento: " + (e?.response?.data?.error || e.message));
    }
  };

  // "Registrar resultado" → perdida (con motivo). Ganada va por handleAprobar.
  const handlePerdida = async () => {
    if (!resTarget?._id) return;
    try {
      await backend.patch(`quotation/${resTarget._id}/estado`, {
        status: "perdida",
        motivo: resMotivo,
        detalle: resDetalle.trim() || undefined,
      });
      setQuotations(prev => prev.map(x =>
        x._id === resTarget._id
          ? { ...x, status: "perdida", resultado: { motivo: resMotivo, detalle: resDetalle.trim(), fecha: new Date().toISOString() } }
          : x
      ));
      setResTarget(null);
      setResMotivo("precio");
      setResDetalle("");
      reloadPendientes();
    } catch (e) {
      alert("Error al registrar resultado: " + (e?.response?.data?.error || e.message));
    }
  };

  // F5.8 — Aprobar: cotizada → aprobada, WO espejo cotizado → working
  const handleAprobar = async () => {
    if (!convertTarget?._id) return;
    try {
      const res = await backend.patch(
        `quotation/${convertTarget._id}/aprobar`,
        {
          ...(ocInput.trim() ? { ocNumber: ocInput.trim() } : {}),
          // T2e — checklist de arranque (opcionales)
          ...(deadlineInput ? { deliveryDeadline: deadlineInput } : {}),
          ...(materialInput.trim() ? { materialProveedor: materialInput.trim() } : {}),
        }
      );
      if (res.data?.quotation) {
        const upd = res.data.quotation;
        setQuotations(prev => prev.map(q =>
          q._id === convertTarget._id
            ? { ...q,
                status: upd.status || "ganada", // ago-27: /aprobar escribe "ganada"
                convertedToWO: true,
                resultado: upd.resultado ?? q.resultado,
                workOrderNumber: res.data.workOrder?.number || q.workOrderNumber,
                hasOC: upd.hasOC ?? q.hasOC,
                ocNumber: upd.ocNumber ?? q.ocNumber,
              }
            : q
        ));
        setConvertTarget(null);
        setOcInput("");
        setDeadlineInput("");
        setMaterialInput("");
        reloadPendientes();
      }
    } catch (e) {
      alert("Error al aprobar: " + (e?.response?.data?.error || e.message));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    // 28-ago (auditoría): con el backend recién abierto a seller, un error acá
    // (403 residual, cotización convertida, red) se tragaba en silencio y la
    // fila desaparecía o quedaba a medias sin aviso.
    try {
      await backend.delete(`quotation/${deleteTarget._id}`);
      setQuotations(prev => prev.filter(q => q._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch (e) {
      alert("Error al eliminar la cotización: " + (e?.response?.data?.error || e.message));
    }
  };

  // F5.8 — Cerrar cotización con documento (factura/guía/etc.)
  const handleCerrar = async () => {
    if (!closeTarget?._id) return;
    if (!closeDocNumber.trim()) {
      alert("Ingresa el número del documento.");
      return;
    }
    try {
      const res = await backend.patch(`quotation/${closeTarget._id}/cerrar`, {
        docType:   closeDocType,
        docNumber: closeDocNumber.trim(),
      });
      if (res.data?.quotation) {
        const upd = res.data.quotation;
        setQuotations(prev => prev.map(q =>
          q._id === closeTarget._id
            ? { ...q, status: "terminada", closingDoc: upd.closingDoc }
            : q
        ));
        setCloseTarget(null);
        setCloseDocType("factura");
        setCloseDocNumber("");
      }
    } catch (e) {
      alert("Error al cerrar: " + (e?.response?.data?.error || e.message));
    }
  };

  const handleAttachOC = async () => {
    if (!attachOCTarget?._id || !ocInput.trim()) return;
    await backend.patch(`quotation/${attachOCTarget._id}`, {
      hasOC: true,
      ocNumber: ocInput.trim(),
    });
    const num = ocInput.trim();
    setQuotations(prev => prev.map(q =>
      q._id === attachOCTarget._id ? { ...q, hasOC: true, ocNumber: num } : q
    ));
    setAttachOCTarget(null);
    setOcInput("");
  };

  // D-T51b — Link directo desde /pulido
  const pulidoLink = usePulidoLink({
    rows: quotations,
    getNumber: r => r.number,
  });

  const handleSave = async (id, draft) => {
    await backend.patch(`quotation/${id}`, {
      client:       draft.client?._id || draft.client,
      description:  draft.description,
      observations: draft.observations || undefined,
      validityDays: draft.validityDays,
      paymentTerms: draft.paymentTerms,
      // Condiciones del PDF (26-ago): plazo/lugar editables también acá.
      // El PATCH del backend hace merge campo a campo, y quotationPdf usa
      // condicionesPago/validez de los campos legacy si no vienen acá.
      condiciones: {
        ...(draft.condiciones?.plazoEntrega != null ? { plazoEntrega: draft.condiciones.plazoEntrega } : {}),
        ...(draft.condiciones?.lugarEntrega != null ? { lugarEntrega: draft.condiciones.lugarEntrega } : {}),
        // pago/validez se editan en sus campos legacy — se espejan acá para
        // que el PDF (que prefiere condiciones.*) nunca muestre un valor viejo
        ...(draft.paymentTerms  != null ? { condicionesPago: draft.paymentTerms } : {}),
        ...(draft.validityDays  != null ? { validez: `${draft.validityDays} días` } : {}),
      },
      status:       draft.status,
      hasOC:        draft.hasOC,
      ocNumber:     draft.ocNumber || undefined,
      items: (draft.items || []).map(it => ({
        piece:     it.pieceId || undefined,
        name:      it.desc    || "",
        quantity:  it.qty     || 1,
        unitPrice: it.pUnit   || 0,
        sku:       it.sku     || undefined,
        desc:      it.desc    || undefined,
        qty:       it.qty     || 1,
        pUnit:     it.pUnit   || 0,
        source:    it.source  || undefined,
        pieceId:   it.pieceId || undefined,
        // Paquete A — clasificación por línea (el server preserva el
        // codigoTrabajo existente y asigna correlativo a líneas fab/rep nuevas)
        tipoTrabajo:        it.tipoTrabajo || "ref",
        codigoTrabajo:      it.codigoTrabajo || undefined,
        componenteReparado: it.componenteReparado || undefined,
        planos:             it.planos || [],
      })),
    });
    setQuotations(prev => prev.map(q => q._id === id ? { ...q, ...draft } : q));
  };

  // ── ExpandableTable config ────────────────────────────────────────────────

  const columns = [
    {
      key: "number", label: "N°", width: 120,
      render: (_, row) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
          {row.number ? (
            <span className="bini-mono" style={{
              color: "#a78bfa", background: "#1f1635",
              padding: "2px 8px", borderRadius: 4,
              fontSize: 11, fontWeight: 600,
              border: "1px solid #2d2050",
              whiteSpace: "nowrap",
            }}>
              N° {row.number}
            </span>
          ) : (
            <span style={{ fontSize: 10, color: "#999", fontStyle: "italic" }}>—</span>
          )}
          {/* Paquete A: badge del tipo predominante (solo si hay algo especial) */}
          {tipoPredominante(row) !== "ref" && <TipoBadge tipo={tipoPredominante(row)} />}
        </span>
      ),
    },
    {
      key: "client", label: "Cliente", width: 150,
      render: (_, row) => row.client?.name || "—",
    },
    {
      key: "description", label: "Descripción",
      render: (_, row) => (
        <span style={{ color: "#bbb" }}>
          {row.description || (row.items || []).map(it => it.desc || it.sku).filter(Boolean).join(", ") || "—"}
        </span>
      ),
    },
    {
      key: "ocNumber", label: "N° OC cliente", width: 120,
      render: (_, row) => row.ocNumber ? (
        <span className="bini-mono" style={{
          color: "#3498db", background: "#0f2230",
          padding: "2px 8px", borderRadius: 4,
          fontSize: 11, fontWeight: 600,
          border: "1px solid #1d4060",
          whiteSpace: "nowrap",
        }} title={`OC del cliente: ${row.ocNumber}`}>
          {row.ocNumber}
        </span>
      ) : (
        <span style={{ fontSize: 10, color: "#999", fontStyle: "italic" }}>sin OC</span>
      ),
    },
    {
      key: "total", label: "Total", width: 110, align: "right",
      render: (_, row) => (
        <span style={{ color: "var(--bini-green)", fontWeight: 600 }}>
          {fmtMoney(row.total)}
        </span>
      ),
    },
    {
      key: "createdAt", label: "Fecha", width: 75,
      render: (_, row) => fmt(row.createdAt),
    },
    {
      key: "status", label: "Estado", width: 150,
      render: (_, row) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <QBadge status={row.status || "cotizada"} />
          {isCotizada(row) && (
            <button
              title="Marcar como enviada al cliente (arranca el reloj de seguimiento)"
              onClick={e => { e.stopPropagation(); marcarEnviada(row); }}
              style={{
                background: "#0f2230", color: "#3498db", border: "1px solid #1d4060",
                borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700,
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              ✉ Enviada
            </button>
          )}
        </span>
      ),
    },
  ];

  const renderDetail = (data, helpers) => {
    // F5.8 — estados nuevos. isTerminada bloquea edición de ítems.
    const isTerm  = isTerminada(data);
    const isAprob = isAprobada(data);            // ganada/aprobada (OT activa)
    const isCot   = isCotizada(data);
    const isActiva = isActivaComercial(data);    // cotizada/enviada/seguimiento/vencida
    const puedeSeguir = isEnviada(data) || isSeguimiento(data);
    // legacy compat
    const isConverted = isAprob || isTerm;
    const subtotal = (data.items || []).reduce(
      (s, it) => s + (it.pUnit || 0) * (it.qty || 1), 0
    );
    const iva   = Math.round(subtotal * 0.19);
    const total = subtotal + iva;

    return (
      <div className="bini-expand">

        {/* header del detalle */}
        <div className="bini-expand-header">
          <div>
            <div className="bini-expand-title">
              Cotización N° {data.number || "—"}
            </div>
            <div className="bini-expand-sub">
              {data.client?.name || "—"} · {fmt(data.createdAt)}
              {isConverted && data.workOrderRef && (
                <>
                  {" · "}
                  <a
                    href={`/workOrders?openId=${data.workOrderRef}`}
                    onClick={e => {
                      e.stopPropagation(); e.preventDefault();
                      navigate(`/workOrders?openId=${data.workOrderRef}`);
                    }}
                    style={{
                      color: "#3498db", textDecoration: "none",
                      fontFamily: "monospace", fontWeight: 600, cursor: "pointer",
                    }}
                    title="Ir a la OT espejo en /workOrders"
                  >OT-{data.workOrderNumber}</a>
                  {" creada"}
                </>
              )}
              {isConverted && !data.workOrderRef && ` · OT-${data.workOrderNumber} creada`}
            </div>
          </div>
          <div className="bini-expand-actions">
            {!helpers.isEditing && (
              <>
                <button
                  className="bini-btn"
                  disabled={pdfLoadingId === data._id}
                  onClick={e => { e.stopPropagation(); handleVerPdf(data); }}
                  title="Ver / descargar / reenviar el PDF de esta cotización"
                >
                  {pdfLoadingId === data._id ? "Generando…" : "📄 Ver PDF"}
                </button>
                <button
                  className="bini-btn bini-btn-edit"
                  onClick={e => { e.stopPropagation(); helpers.startEdit(); }}
                >
                  ✎ Editar
                </button>
                {!isConverted && (
                  <button
                    className="bini-btn-icon danger"
                    onClick={e => { e.stopPropagation(); setDeleteTarget(data); }}
                  >
                    Eliminar
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* campos: 3 columnas (className="grid" colapsa a 2/1 en móvil) */}
        <div className="grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div className="bini-field">
            <label>Cliente</label>
            {helpers.isEditing ? (
              <select
                className="bini-input"
                name="client"
                value={data.client?._id || data.client || ""}
                onChange={e => {
                  const c = clients.find(cl => cl._id === e.target.value);
                  helpers.updateDraft(d => ({ ...d, client: c || { _id: e.target.value } }));
                }}
              >
                <option value="">Sin cliente</option>
                {clients.map(c => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            ) : (
              <div className="val">{data.client?.name || "—"}</div>
            )}
          </div>

          <div className="bini-field">
            <label>Fecha</label>
            <div className="val">{fmt(data.createdAt)}</div>
          </div>

          <div className="bini-field">
            <label>Estado</label>
            {helpers.isEditing ? (
              <select
                className="bini-input"
                name="status"
                value={data.status || "open"}
                onChange={e => helpers.updateDraft(d => ({ ...d, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <div className="val"><QBadge status={data.status || "cotizada"} /></div>
            )}
          </div>

          <div className="bini-field" style={{ gridColumn: "1 / -1" }}>
            <label>Descripción</label>
            {helpers.isEditing ? (
              <input
                className="bini-input"
                name="description"
                value={data.description || ""}
                onChange={e => helpers.updateDraft(d => ({ ...d, description: e.target.value }))}
                placeholder="Ej: Mordazas 480 x40 + Spacer x8"
              />
            ) : (
              <div className="val">{data.description || "—"}</div>
            )}
          </div>

          <div className="bini-field">
            <label>Validez (días)</label>
            {helpers.isEditing ? (
              <input
                className="bini-input r"
                type="number"
                value={data.validityDays || 30}
                onChange={e => helpers.updateDraft(d => ({ ...d, validityDays: Number(e.target.value) }))}
              />
            ) : (
              <div className="val">{data.validityDays || 30} días</div>
            )}
          </div>

          <div className="bini-field">
            <label>Cond. pago</label>
            {helpers.isEditing ? (
              <select
                className="bini-input"
                value={data.paymentTerms || "30 días"}
                onChange={e => helpers.updateDraft(d => ({ ...d, paymentTerms: e.target.value }))}
              >
                {/* si el valor guardado no está en la lista (texto libre del form nuevo), mostrarlo igual */}
                {(PAYMENT_TERMS.includes(data.paymentTerms) || !data.paymentTerms ? PAYMENT_TERMS : [data.paymentTerms, ...PAYMENT_TERMS])
                  .map(o => <option key={o}>{o}</option>)}
              </select>
            ) : (
              <div className="val">{data.paymentTerms || "—"}</div>
            )}
          </div>

          {/* Plazo y lugar de entrega — condiciones del PDF (26-ago, req. Nico).
              Editables acá igual que en el form de creación; texto libre con
              sugerencias (datalist). El PDF server-side las lee de condiciones. */}
          <div className="bini-field">
            <label>Plazo de entrega</label>
            {helpers.isEditing ? (
              <>
                <input
                  className="bini-input"
                  list="plazos-entrega-opciones"
                  value={data.condiciones?.plazoEntrega ?? "5 a 6 días hábiles"}
                  onChange={e => helpers.updateDraft(d => ({
                    ...d, condiciones: { ...(d.condiciones || {}), plazoEntrega: e.target.value },
                  }))}
                  placeholder="Ej: 10 días hábiles"
                />
                <datalist id="plazos-entrega-opciones">
                  {PLAZOS_ENTREGA.map(o => <option key={o} value={o} />)}
                </datalist>
              </>
            ) : (
              <div className="val">{data.condiciones?.plazoEntrega || "5 a 6 días hábiles"}</div>
            )}
          </div>

          <div className="bini-field">
            <label>Lugar de entrega</label>
            {helpers.isEditing ? (
              <input
                className="bini-input"
                value={data.condiciones?.lugarEntrega ?? "sus bodegas"}
                onChange={e => helpers.updateDraft(d => ({
                  ...d, condiciones: { ...(d.condiciones || {}), lugarEntrega: e.target.value },
                }))}
                placeholder="Ej: sus bodegas"
              />
            ) : (
              <div className="val">{data.condiciones?.lugarEntrega || "sus bodegas"}</div>
            )}
          </div>

          {helpers.isEditing && (
            <div className="bini-field" style={{ gridColumn: "1 / -1" }}>
              <label>Observaciones</label>
              <textarea
                className="bini-input"
                rows={2}
                value={data.observations || ""}
                onChange={e => helpers.updateDraft(d => ({ ...d, observations: e.target.value }))}
                placeholder="Notas internas..."
                style={{ resize: "vertical", minHeight: 44 }}
              />
            </div>
          )}
        </div>

        {/* ítems */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", margin: "14px 0 6px",
        }}>
          <span className="bini-section-title" style={{ margin: 0 }}>
            Ítems ({data.items?.length || 0})
          </span>
          {helpers.isEditing && !isConverted && (
            <button
              className="bini-btn-add"
              onClick={e => {
                e.stopPropagation();
                helpers.updateDraft(d => ({
                  ...d,
                  items: [...(d.items || []), {
                    sku: "", desc: "", qty: 1, pUnit: 0, source: null, pieceId: null,
                  }],
                }));
              }}
            >
              + Agregar ítem
            </button>
          )}
        </div>

        <table className="bini-items-table">
          <colgroup>{[140, null, 50, 90, 100].map((w, i) => (
            <col key={i} style={w ? { width: w } : undefined} />
          ))}{helpers.isEditing && !isConverted && <col style={{ width: 30 }} />}</colgroup>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Descripción</th>
              <th style={{ textAlign: "right" }}>Cant</th>
              <th style={{ textAlign: "right" }}>P. unit</th>
              <th style={{ textAlign: "right" }}>Subtotal</th>
              {helpers.isEditing && !isConverted && <th />}
            </tr>
          </thead>
          <tbody>
            {(data.items || []).length === 0 && (
              <tr>
                <td
                  colSpan={helpers.isEditing && !isConverted ? 6 : 5}
                  style={{ textAlign: "center", color: "var(--bini-text-muted)", padding: 16 }}
                >
                  Sin ítems
                </td>
              </tr>
            )}
            {(data.items || []).map((it, idx) => {
              const sub = (it.pUnit || 0) * (it.qty || 1);

              const esFabRepItem = it.tipoTrabajo === "fab" || it.tipoTrabajo === "rep";

              if (helpers.isEditing && !isConverted) {
                return (
                  <tr key={idx} data-item-idx={idx}>
                    <td colSpan={2} style={{ padding: "4px 6px" }}>
                      {esFabRepItem ? (
                        /* Línea FAB/REP: descripción libre (sin buscador de catálogo).
                           El codigoTrabajo no se edita — lo asigna el server. */
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <TipoBadge tipo={it.tipoTrabajo} />
                          {it.codigoTrabajo && (
                            <span className="bini-mono" style={{ fontSize: 10, color: "#cbd5e1", whiteSpace: "nowrap" }}>
                              {it.codigoTrabajo}
                            </span>
                          )}
                          <input
                            className="bini-input"
                            value={it.desc || ""}
                            placeholder={it.tipoTrabajo === "rep" ? "Descripción de la reparación" : "Descripción del trabajo"}
                            onChange={e => helpers.updateDraft(d => {
                              const items = [...(d.items || [])];
                              items[idx] = { ...items[idx], desc: e.target.value };
                              return { ...d, items };
                            })}
                            style={{ flex: 1 }}
                          />
                          {it.tipoTrabajo === "rep" && (
                            <input
                              className="bini-input"
                              value={it.componenteReparado || ""}
                              placeholder="Componente reparado"
                              onChange={e => helpers.updateDraft(d => {
                                const items = [...(d.items || [])];
                                items[idx] = { ...items[idx], componenteReparado: e.target.value };
                                return { ...d, items };
                              })}
                              style={{ width: 160 }}
                            />
                          )}
                        </div>
                      ) : (
                        <PieceLookup
                          name="sku"
                          value={it.sku || it.desc ? {
                            pieceId:     it.pieceId || null,
                            sku:         it.sku     || "",
                            description: it.desc    || "",
                            price:       it.pUnit   || 0,
                            source:      it.source  || null,
                            temporary:   !!it.temporary,
                            expiresAt:    it.expiresAt || null,
                          } : null}
                          onChange={updated => helpers.updateDraft(d => {
                            const items = [...(d.items || [])];
                            items[idx] = {
                              ...items[idx],
                              sku:     updated?.sku         || "",
                              desc:    updated?.description || "",
                              pUnit:   updated?.price       ?? items[idx].pUnit,
                              source:  updated?.source      || null,
                              pieceId: updated?.pieceId     || null,
                              temporary: !!updated?.temporary,
                              expiresAt: updated?.expiresAt || null,
                            };
                            return { ...d, items };
                          })}
                        />
                      )}
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input
                        className="bini-items-table r"
                        type="number" min={1}
                        name="qty"
                        value={it.qty || 1}
                        onChange={e => helpers.updateDraft(d => {
                          const items = [...(d.items || [])];
                          items[idx] = { ...items[idx], qty: Number(e.target.value) };
                          return { ...d, items };
                        })}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input
                        className="bini-items-table r"
                        type="number" min={0}
                        name="pUnit"
                        value={it.pUnit || 0}
                        onChange={e => helpers.updateDraft(d => {
                          const items = [...(d.items || [])];
                          items[idx] = { ...items[idx], pUnit: Number(e.target.value) };
                          return { ...d, items };
                        })}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td className={`price ${!sub ? "zero" : ""}`}>
                      {sub ? `$${fmtN(sub)}` : "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="bini-btn-icon danger"
                        style={{ padding: "2px 5px", fontSize: 11 }}
                        onClick={() => helpers.updateDraft(d => ({
                          ...d,
                          items: (d.items || []).filter((_, i) => i !== idx),
                        }))}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={idx}>
                  <td>
                    {esFabRepItem ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                        <TipoBadge tipo={it.tipoTrabajo} />
                        <span className="bini-mono" style={{ color: "#cbd5e1", fontSize: 11 }}>
                          {it.codigoTrabajo || "—"}
                        </span>
                      </span>
                    ) : (
                      <span className="bini-mono" style={{ color: "var(--bini-blue)", fontSize: 11 }}>
                        {it.sku || "—"}
                      </span>
                    )}
                  </td>
                  <td className="desc">
                    {it.desc || "—"}
                    {esFabRepItem && it.componenteReparado && (
                      <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>
                        🔧 Componente: {it.componenteReparado}
                      </div>
                    )}
                    {esFabRepItem && (it.planos || []).length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 3 }}>
                        {it.planos.map((p, k) => (
                          <a
                            key={p.key || k}
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                              fontSize: 10, color: "#3498db", textDecoration: "none",
                              background: "#0f2230", border: "1px solid #1d4060",
                              borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap",
                            }}
                            title={`Abrir plano: ${p.nombre}`}
                          >
                            📄 {String(p.nombre || "plano").slice(0, 30)}
                          </a>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="num">{it.qty || 1}</td>
                  <td className={`price ${!it.pUnit ? "zero" : ""}`}>
                    {it.pUnit ? `$${fmtN(it.pUnit)}` : (
                      <span
                        title={
                          it.pieceId
                            ? "Click para cargar precio en catálogo"
                            : "Item sin SKU. Crear pieza en catálogo o ver detalle en /pulido"
                        }
                        style={{
                          color: "#cbd5e1",
                          fontStyle: "italic",
                          cursor: "pointer",
                          borderBottom: "1px dashed #888",
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          if (it.sku) {
                            navigate(`/pieces?search=${encodeURIComponent(it.sku)}`);
                          } else if (it.pieceId) {
                            navigate(`/pieces?search=${it.pieceId}`);
                          } else if (puedeVerPulido) {
                            navigate("/pulido#items-pendientes");
                          } else {
                            // /pulido es solo-admin: al resto lo mandamos al catálogo
                            navigate("/pieces?search=");
                          }
                        }}
                      >
                        sin precio
                      </span>
                    )}
                  </td>
                  <td className={`price ${!sub ? "zero" : ""}`}>
                    {sub ? `$${fmtN(sub)}` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Ciclo F1 (31-ago-2026) — el respaldo del material.
            Va ANTES de los totales a propósito: es de dónde salió el número, y el
            vendedor lo está mirando justo cuando arma el precio. Después aparece solo
            en la OT espejo, para que producción sepa qué se cotizó y con quién antes de
            fabricar. */}
        <div style={{
          background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6,
          padding: "10px 12px", marginBottom: 12,
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: "#cbd5e1",
            textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6,
          }}>
            💰 Cotización de material del proveedor
          </div>
          <div style={{ fontSize: 11, color: "#8a8a8a", marginBottom: 8 }}>
            El PDF que te pasó el proveedor. Queda pegado a esta cotización y lo ve
            producción en la OT antes de fabricar.
          </div>
          <DocumentAttacher
            refField="quotationRefs"
            refId={data._id}
            defaultType="cotizacionProveedor"
          />

          {/* Ciclo F2 — pedirle el precio formalmente, en vez de por correo suelto. */}
          <div style={{ marginTop: 8 }}>
            <PedirCotizacionMaterial quotationId={data._id} />
          </div>
        </div>

        {/* totales */}
        <div className="bini-totals">
          <div className="bini-totals-box">
            <div className="bini-totals-row">
              <span>Subtotal neto</span>
              <span className="v">${fmtN(subtotal)}</span>
            </div>
            <div className="bini-totals-row">
              <span>IVA (19%)</span>
              <span className="v">${fmtN(iva)}</span>
            </div>
            <div className="bini-totals-row total">
              <span>Total c/IVA</span>
              <span className="v">${fmtN(total)}</span>
            </div>
          </div>
        </div>

        {/* success boxes */}
        {data.hasOC && (
          <div className="bini-success-box">
            <div className="t">✓ OC de cliente adjuntada</div>
            <div className="d">N° OC: {data.ocNumber || "—"}</div>
          </div>
        )}
        {isAprob && (
          <div className="bini-success-box">
            <div className="t">✓ Aprobada · OT N°{data.workOrderNumber || data.number} en producción</div>
            <div className="d">Los ítems están bloqueados. Cierra con factura/guía para finalizar.</div>
          </div>
        )}
        {isTerm && data.closingDoc?.number && (
          <div className="bini-success-box">
            <div className="t">✓ Terminada</div>
            <div className="d">
              Cerrada con {CLOSING_DOC_TYPES.find(t => t.value === data.closingDoc.type)?.label || data.closingDoc.type}
              {" "}N° <strong>{data.closingDoc.number}</strong>
              {data.closingDoc.date && <> · {fmt(data.closingDoc.date)}</>}
            </div>
          </div>
        )}

        {/* Paquete B — resultado perdida (motivo) */}
        {isPerdida(data) && data.resultado?.motivo && (
          <div style={{
            background: "#2a0d0d", border: "1px solid #4a1a1a", borderRadius: 6,
            padding: "8px 12px", marginTop: 10, fontSize: 11, color: "#e0a0a0",
          }}>
            ✕ Perdida ({MOTIVOS_PERDIDA.find(m => m.value === data.resultado.motivo)?.label || data.resultado.motivo})
            {data.resultado.detalle && <> — {data.resultado.detalle}</>}
            {data.resultado.fecha && <> · {fmt(data.resultado.fecha)}</>}
          </div>
        )}

        {/* Paquete B — historial de gestiones */}
        {(data.seguimientos || []).length > 0 && (
          <div style={{ marginTop: 12 }}>
            <span className="bini-section-title" style={{ margin: 0 }}>
              Gestiones de seguimiento ({data.seguimientos.length})
            </span>
            <div style={{ marginTop: 6 }}>
              {[...data.seguimientos].reverse().map((s, i) => (
                <div key={i} style={{
                  display: "flex", gap: 8, padding: "4px 0",
                  borderBottom: "1px solid #252525", fontSize: 11, color: "#bbb",
                }}>
                  <span style={{ color: "#f39c12", whiteSpace: "nowrap" }}>{fmt(s.fecha)}</span>
                  <span style={{ flex: 1 }}>{s.nota}</span>
                  {s.por && <span style={{ color: "#777", whiteSpace: "nowrap" }}>{s.por}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* F5.8 — action bar en modo lectura: depende del estado actual */}
        {!helpers.isEditing && !isTerm && (
          <div className="bini-action-bar">
            <div className="bini-action-bar-l">
              {isCot && (
                <button
                  className="bini-btn"
                  onClick={e => { e.stopPropagation(); marcarEnviada(data); }}
                  title="Marcar como enviada al cliente — arranca el reloj de seguimiento de 48h"
                >
                  ✉ Marcar enviada
                </button>
              )}
              {puedeSeguir && (
                <button
                  className="bini-btn"
                  onClick={e => { e.stopPropagation(); setSegTarget(data); }}
                  title="Registrar una gestión (llamada, correo, visita)"
                >
                  ✓ Seguimiento hecho
                </button>
              )}
              {isActiva && !isCot && (
                <button
                  className="bini-btn bini-btn-cancel"
                  onClick={e => { e.stopPropagation(); setResTarget(data); }}
                  title="Cerrar el ciclo comercial: perdida (con motivo)"
                >
                  ✕ Marcar perdida
                </button>
              )}
              {isActiva && (
                <button
                  className="bini-btn bini-btn-success"
                  onClick={e => { e.stopPropagation(); setConvertTarget(data); }}
                  title="Cliente aprobó: pasa a producción activa (ganada)"
                >
                  ✓ Aprobar cotización
                </button>
              )}
              {isAprob && !data.hasOC && (
                <button
                  className="bini-btn bini-btn-save"
                  onClick={e => { e.stopPropagation(); setAttachOCTarget(data); }}
                  title="Adjuntar N° OC del cliente (opcional)"
                >
                  📎 Adjuntar N° OC
                </button>
              )}
              {isAprob && (
                <button
                  className="bini-btn bini-btn-success"
                  onClick={e => { e.stopPropagation(); setCloseTarget(data); }}
                  title="Trabajo terminado: cierra con factura o guía"
                >
                  ✓ Cerrar cotización
                </button>
              )}
            </div>
            <div className="bini-action-bar-r" />
          </div>
        )}

        {/* action bar en modo edición */}
        {helpers.isEditing && (
          <>
            {isConverted && (
              <div className="bini-warning">
                <div className="t">⚠ Cotización convertida a OT-{data.workOrderNumber}</div>
                <div className="d">Los ítems están bloqueados. Puedes editar datos generales (estado, condiciones, observaciones).</div>
              </div>
            )}
            <div className="bini-action-bar">
              <div className="bini-action-bar-l" />
              <div className="bini-action-bar-r">
                <button
                  className="bini-btn bini-btn-cancel"
                  onClick={e => { e.stopPropagation(); helpers.cancelEdit(); }}
                >
                  Cancelar
                </button>
                <button
                  className="bini-btn bini-btn-save"
                  disabled={helpers.isSaving}
                  onClick={e => { e.stopPropagation(); helpers.saveEdit(); }}
                >
                  {helpers.isSaving ? "Guardando…" : "Guardar cambios"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div style={S.wrap}>

      {/* header */}
      <div style={{
        display: "flex", flexDirection: "row",
        alignItems: "center", justifyContent: "space-between", marginBottom: 18,
        flexWrap: "wrap", gap: 8, // móvil
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Cotizaciones</div>
        <button
          onClick={() => navigate("/quotations/new")}
          style={{
            background: "#e30613", color: "#fff", border: "none",
            borderRadius: 6, padding: "7px 16px", fontSize: 12,
            fontWeight: 700, cursor: "pointer",
          }}
        >
          + Nueva cotización
        </button>
      </div>

      {/* 🫀 Pulso de ventas (28-ago) — visión comercial del vendedor: facturado
          del mes con comparaciones, pipeline vivo, clientes dormidos y top del
          mes. Solo admin/accountant/seller (adentro se auto-oculta para el
          resto). Los KPIs de COTIZACIONES de abajo se mantienen: miden otra
          cosa (lo cotizado/ganado del funnel, no lo facturado) y filtran la tabla. */}
      <PulsoVentas
        onFiltrar={key => setActiveFilter(prev => (prev === key ? "all" : key))}
        onBuscarCliente={nombre => { setSearchQuery(nombre || ""); setActiveFilter("all"); }}
      />

      {/* KPIs de cotizaciones (ago-27, tarjetas clicables: filtran la tabla).
          28-ago: estilo estándar (tarjeta #232323/#2e2e2e radio 12, label
          10px/700 #8a8a8a, tabular-nums) + "% conversión" filtra "ganadas". */}
      <div style={{ display: "flex", flexDirection: "row", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          { key: "cotizado_mes", label: "Cotizado del mes", value: fmtMoney(cotizadoMesMonto), sub: `${cotizadoMes.length} cotización${cotizadoMes.length === 1 ? "" : "es"} · neto — IVA aparte: ${fmtMoney(cotizadoMesIva)}`, color: "#3498db", clickable: true },
          { key: "ganado_mes",   label: "Ganado del mes",   value: fmtMoney(ganadoMesMonto),   sub: `${ganadasMes.length} ganada${ganadasMes.length === 1 ? "" : "s"} · neto — IVA aparte: ${fmtMoney(ganadoMesIva)}`,       color: "#2ecc71", clickable: true },
          { key: "ganadas",      label: "% conversión",     value: conversion + "%",           sub: `${ganadasAnio} ganadas de ${enviadasAnio} enviadas (${anioActual})`, color: conversion >= 50 ? "#2ecc71" : "#f39c12", clickable: true, titulo: "Filtrar: cotizaciones ganadas" },
          { key: "por_seguir",   label: "Por seguir",       value: pendientes.length,          sub: "48h+ sin gestión — llamar hoy", color: pendientes.length > 0 ? "#e30613" : "#7f8c8d", clickable: true },
        ].map(m => {
          const isActive = m.clickable && activeFilter === m.key;
          return (
            <div
              key={m.key}
              onClick={() => m.clickable && setActiveFilter(prev => prev === m.key ? "all" : m.key)}
              title={m.titulo || (m.clickable ? `Filtrar: ${m.label}` : undefined)}
              style={{
                flex: "1 1 0", minWidth: 150,
                background: isActive ? "#2a2a2a" : "#232323",
                border: `1px solid ${isActive ? m.color : "#2e2e2e"}`,
                borderRadius: 12, padding: "12px 14px",
                cursor: m.clickable ? "pointer" : "default",
                userSelect: "none",
                transition: "background .15s, border-color .15s",
              }}
              onMouseEnter={e => { if (m.clickable && !isActive) e.currentTarget.style.background = "#2a2a2a"; }}
              onMouseLeave={e => { if (m.clickable && !isActive) e.currentTarget.style.background = "#232323"; }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: isActive ? m.color : "#8a8a8a", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".05em" }}>{m.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: m.color, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{m.value}</div>
              <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>{m.sub}</div>
            </div>
          );
        })}
      </div>

      {/* 💰 Salud de precios (28-ago) — panel colapsable junto a los KPIs.
          Cada pieza con su precio vigente, desde cuándo no cambia y la erosión
          vs UF: la munición para defender los precios al cotizar.
          Solo roles con acceso al endpoint (admin/accountant/seller). */}
      {puedeVerSalud && (
      <div style={{ marginBottom: 14 }}>
        <button
          onClick={() => setShowSalud(s => !s)}
          style={{
            background: showSalud ? "#2a2a2a" : "#242424",
            color: showSalud ? "#f39c12" : "#cbd5e1",
            border: `1px solid ${showSalud ? "#f39c12" : "#2e2e2e"}`,
            borderRadius: 8, padding: "9px 16px", minHeight: 38,
            fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}
          title="Cada pieza con su precio, desde cuándo no cambia y cuánto subió la UF desde entonces"
        >
          💰 Salud de precios {showSalud ? "▲" : "▼"}
        </button>
        {showSalud && (
          <div style={{ marginTop: 10 }}>
            <SaludPrecios />
          </div>
        )}
      </div>
      )}

      {/* 📞 Por seguir hoy — cotizaciones enviadas con 48h+ sin gestión */}
      {pendientes.length > 0 && (
        <div style={{
          background: "#241a12", border: "1px solid #4a3410", borderRadius: 8,
          padding: "12px 14px", marginBottom: 16,
        }}>
          <div onClick={() => setShowSeguir(v => !v)} style={{
            fontSize: 11, fontWeight: 700, color: "#f39c12",
            textTransform: "uppercase", letterSpacing: ".06em",
            marginBottom: showSeguir ? 8 : 0, cursor: "pointer", userSelect: "none",
          }} title="Mostrar / ocultar la lista">
            📞 Por seguir hoy ({pendientes.length}) {showSeguir ? "▲" : "▼"}
          </div>
          {showSeguir && pendientes.map(p => {
            const dias = Math.floor((p.horasSinGestion || 0) / 24);
            const full = quotations.find(q => String(q._id) === String(p._id));
            return (
              <div key={p._id} style={{
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                padding: "6px 0", borderBottom: "1px solid #33270f", fontSize: 12,
              }}>
                <span className="bini-mono" style={{ color: "#a78bfa", fontWeight: 700, whiteSpace: "nowrap" }}>
                  N° {p.number}
                </span>
                <span style={{ color: "#ddd", flex: 1, minWidth: 120 }}>
                  {p.client?.name || "—"}
                  {p.description && <span style={{ color: "#888" }}> · {String(p.description).slice(0, 40)}</span>}
                </span>
                <span style={{ color: "var(--bini-green)", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {fmtMoney(p.total)}
                </span>
                <span style={{
                  color: (p.horasSinGestion || 0) >= 72 ? "#e74c3c" : "#f39c12",
                  fontWeight: 700, whiteSpace: "nowrap", fontSize: 11,
                }}>
                  {dias >= 1 ? `${dias} día${dias > 1 ? "s" : ""}` : `${p.horasSinGestion}h`} sin gestión
                </span>
                <span style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => setSegTarget(full || p)}
                    style={{
                      background: "#2a1d08", color: "#f39c12", border: "1px solid #4a3410",
                      borderRadius: 5, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                    }}
                    title="Registrar la gestión que hiciste (resetea el reloj de 48h)"
                  >
                    ✓ Seguimiento hecho
                  </button>
                  <button
                    onClick={() => setResTarget(full || p)}
                    style={{
                      background: "#1e1e1e", color: "#cbd5e1", border: "1px solid #444",
                      borderRadius: 5, padding: "4px 10px", fontSize: 11, cursor: "pointer",
                    }}
                    title="Registrar el resultado: ganada o perdida"
                  >
                    Registrar resultado
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* chips de filtro: status del funnel + tipo de trabajo */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4, alignItems: "center" }}>
        {STATUS_CHIP_FILTERS.map(c => {
          const activo = activeFilter === c.key;
          const meta = { cotizadas: "cotizada", enviadas: "enviada", seguimiento: "seguimiento", ganadas: "ganada", perdidas: "perdida", vencidas: "vencida", terminadas: "terminada" }[c.key];
          const color = meta ? STATUS_META[meta].color : "#ccc";
          const n = c.key === "all" ? total : quotations.filter(c.test).length;
          return (
            <button
              key={c.key}
              onClick={() => setActiveFilter(prev => prev === c.key ? "all" : c.key)}
              style={{
                background: activo ? "#2a2a2a" : "#1e1e1e",
                color: activo ? color : "#999",
                border: `1px solid ${activo ? color : "#2e2e2e"}`,
                borderRadius: 5, padding: "3px 9px", fontSize: 10,
                fontWeight: activo ? 700 : 400, cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {c.label} ({n})
            </button>
          );
        })}
        <span style={{ width: 1, height: 16, background: "#333", margin: "0 4px" }} />
        {["ref", "fab", "rep"].map(t => {
          const activo = filterTipo === t;
          const m = TIPO_META[t];
          return (
            <button
              key={t}
              onClick={() => setFilterTipo(prev => prev === t ? "all" : t)}
              title={m.title}
              style={{
                background: activo ? m.bg : "#1e1e1e",
                color: activo ? m.color : "#999",
                border: `1px solid ${activo ? m.border : "#2e2e2e"}`,
                borderRadius: 5, padding: "3px 9px", fontSize: 10,
                fontWeight: activo ? 700 : 400, cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {/* tabla con expand inline */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, margin: "14px 0 8px", flexWrap: "wrap",
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, color: "#999",
          textTransform: "uppercase", letterSpacing: ".07em",
        }}>
          Cotizaciones registradas
          {isFiltered && (
            <span style={{ color: "#cbd5e1", textTransform: "none", fontWeight: 400, marginLeft: 8, letterSpacing: 0 }}>
              · {filteredQuotations.length} de {quotations.length}
            </span>
          )}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input
            type="search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar N° cot, OT, OC, cliente, SKU…"
            style={{
              background: "#161616", border: "1px solid #333", borderRadius: 5,
              padding: "6px 10px", fontSize: 12, color: "#ddd",
              fontFamily: "Arial,sans-serif", width: "min(280px, 100%)", boxSizing: "border-box",
            }}
          />
        {isFiltered && (
          <button
            onClick={() => { setActiveFilter("all"); setSearchQuery(""); }}
            style={{
              background: "transparent", border: "1px solid #444",
              color: "#aaa", borderRadius: 5, padding: "5px 10px",
              fontSize: 11, cursor: "pointer",
            }}
            title="Limpiar filtros"
          >
            Limpiar filtros
          </button>
        )}
        </div>
      </div>
      <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflow: "hidden", background: "#242424" }}>
        <ExpandableTable
          columns={columns}
          rows={pageRows}
          renderDetail={renderDetail}
          onSave={handleSave}
          emptyText={
            loading
              ? "Cargando…"
              : isFiltered
                ? "Ninguna cotización coincide con el filtro"
                : "Sin cotizaciones registradas"
          }
          initialExpandedId={pulidoLink.openId}
          initialEditingId={pulidoLink.editingId}
        />
      </div>

      {/* paginación 50/pág igual a /trabajos */}
      {totalPages > 1 && (
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginTop: 14, fontSize: 11, color: "#cbd5e1",
          flexWrap: "wrap", gap: 8, // móvil
        }}>
          <div>
            Mostrando {((page - 1) * PER_PAGE) + 1}–{Math.min(page * PER_PAGE, filteredQuotations.length)} de {filteredQuotations.length}
          </div>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <button className="bini-btn-icon"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ opacity: page === 1 ? 0.4 : 1 }}>◂</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .map((p, i, arr) => (
                <React.Fragment key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && (
                    <span style={{ color: "#999", padding: "0 4px" }}>…</span>
                  )}
                  <button className="bini-btn-icon"
                    onClick={() => setPage(p)}
                    style={{
                      background: p === page ? "#3498db" : "#2e2e2e",
                      color: p === page ? "#fff" : "#aaa",
                      minWidth: 28,
                    }}>{p}</button>
                </React.Fragment>
              ))}
            <button className="bini-btn-icon"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{ opacity: page === totalPages ? 0.4 : 1 }}>▸</button>
          </div>
        </div>
      )}

      {/* modal eliminar */}
      <ConfirmModal
        open={!!deleteTarget}
        title={`Eliminar Cotización N° ${deleteTarget?.number || "—"}`}
        description={`Cliente: ${deleteTarget?.client?.name || "—"} · Total: ${fmtMoney(deleteTarget?.total)}`}
        warning="La OT asociada (mismo número) no se eliminará."
        confirmLabel="Eliminar cotización"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* F5.8 — modal aprobar cotización (con OC opcional) */}
      <ConfirmModal
        open={!!convertTarget}
        title={`Aprobar Cotización N° ${convertTarget?.number || "—"}`}
        description={`Cliente: ${convertTarget?.client?.name || "—"} · Total: ${fmtMoney(convertTarget?.total)}`}
        warning={{
          title: `OT N°${convertTarget?.number} pasará a producción activa`,
          description: "Los ítems quedarán bloqueados. OC, PDF/planos, plazo y material son opcionales — pero llenarlos ahora deja el trabajo listo para arrancar.",
        }}
        confirmLabel="✓ Aprobar y activar OT"
        confirmVariant="success"
        onConfirm={handleAprobar}
        onCancel={() => { setConvertTarget(null); setOcInput(""); setDeadlineInput(""); setMaterialInput(""); }}
      >
        <div className="bini-field" style={{ marginBottom: 12 }}>
          <label>N° OC del cliente (opcional)</label>
          <input
            className="bini-input bini-mono"
            value={ocInput}
            onChange={e => setOcInput(e.target.value)}
            placeholder="Ej: OC-45242 (déjalo vacío si no hay OC formal)"
            autoFocus
          />
        </div>
        {/* T2e — checklist de arranque: plazo comprometido + material */}
        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" /* móvil */ }}>
          <div className="bini-field" style={{ flex: 1, minWidth: 150 }}>
            <label>Plazo de entrega comprometido</label>
            <input
              type="date"
              className="bini-input"
              value={deadlineInput}
              onChange={e => setDeadlineInput(e.target.value)}
            />
          </div>
          <div className="bini-field" style={{ flex: 1.4, minWidth: 180 }}>
            <label>Material — proveedor / OC de compra</label>
            <input
              className="bini-input"
              value={materialInput}
              onChange={e => setMaterialInput(e.target.value)}
              placeholder='Ej: "Marzam — barra 4140 Ø3&quot;" o "OC compra 14"'
            />
          </div>
        </div>
        {convertTarget?._id && (
          <div style={{
            marginTop: 12, paddingTop: 12,
            borderTop: "1px solid #2e2e2e",
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#cbd5e1",
              textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6,
            }}>
              📎 Adjuntar PDF de la OC del cliente (opcional)
            </div>
            <DocumentAttacher
              refField="quotationRefs"
              refId={convertTarget._id}
              defaultType="ocCliente"
            />
          </div>
        )}
      </ConfirmModal>

      {/* F5.8 — modal cerrar cotización con documento */}
      <ConfirmModal
        open={!!closeTarget}
        title={`Cerrar Cotización N° ${closeTarget?.number || "—"}`}
        description={`Cliente: ${closeTarget?.client?.name || "—"} · Total: ${fmtMoney(closeTarget?.total)}`}
        warning={{
          title: "El trabajo se marca como terminado",
          description: "La OT espejo se cerrará. Si es factura/boleta/nota crédito → estado 'Facturada'. Si es guía de despacho → estado 'Entregada'.",
        }}
        confirmLabel="✓ Cerrar cotización"
        confirmVariant="success"
        onConfirm={handleCerrar}
        onCancel={() => {
          setCloseTarget(null);
          setCloseDocType("factura");
          setCloseDocNumber("");
        }}
      >
        <div className="bini-field" style={{ marginBottom: 10 }}>
          <label>Tipo de documento</label>
          <select
            className="bini-input"
            value={closeDocType}
            onChange={e => setCloseDocType(e.target.value)}
          >
            {CLOSING_DOC_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="bini-field" style={{ marginBottom: 12 }}>
          <label>N° de documento</label>
          <input
            className="bini-input bini-mono"
            value={closeDocNumber}
            onChange={e => setCloseDocNumber(e.target.value)}
            placeholder="Ej: 12345"
            autoFocus
          />
        </div>
      </ConfirmModal>

      {/* Paquete B — modal "Seguimiento hecho" (nota de gestión) */}
      <ConfirmModal
        open={!!segTarget}
        title={`Seguimiento — Cotización N° ${segTarget?.number || "—"}`}
        description={`Cliente: ${segTarget?.client?.name || "—"} · Total: ${fmtMoney(segTarget?.total)}`}
        confirmLabel="✓ Registrar gestión"
        confirmVariant="success"
        onConfirm={handleSeguimiento}
        onCancel={() => { setSegTarget(null); setSegNota(""); }}
      >
        <div className="bini-field" style={{ marginBottom: 12 }}>
          <label>¿Qué gestión hiciste?</label>
          <textarea
            className="bini-input"
            rows={3}
            value={segNota}
            onChange={e => setSegNota(e.target.value)}
            placeholder="Ej: Llamé a don Pedro — lo revisa con gerencia, quedamos en hablar el viernes."
            style={{ resize: "vertical", minHeight: 60 }}
            autoFocus
          />
        </div>
        <div style={{ fontSize: 10, color: "#999" }}>
          La gestión resetea el reloj de 48h y la cotización pasa a "Seguimiento".
        </div>
      </ConfirmModal>

      {/* Paquete B — modal "Registrar resultado" (ganada / perdida con motivo) */}
      <ConfirmModal
        open={!!resTarget}
        title={`Resultado — Cotización N° ${resTarget?.number || "—"}`}
        description={`Cliente: ${resTarget?.client?.name || "—"} · Total: ${fmtMoney(resTarget?.total)}`}
        confirmLabel="✕ Marcar perdida"
        confirmVariant="danger"
        onConfirm={handlePerdida}
        onCancel={() => { setResTarget(null); setResMotivo("precio"); setResDetalle(""); }}
      >
        <div style={{
          background: "#0d2a15", border: "1px solid #0d4020", borderRadius: 6,
          padding: "10px 12px", marginBottom: 14, display: "flex",
          alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 12, color: "#2ecc71" }}>¿La ganaste? Pasa directo a aprobar la OT.</span>
          <button
            onClick={() => {
              const t = resTarget;
              setResTarget(null); setResMotivo("precio"); setResDetalle("");
              setConvertTarget(t);
            }}
            style={{
              background: "#0d4020", color: "#2ecc71", border: "1px solid #2ecc71",
              borderRadius: 5, padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer",
            }}
          >
            ✓ Ganada — aprobar OT
          </button>
        </div>
        <div className="bini-field" style={{ marginBottom: 10 }}>
          <label>Motivo de la pérdida (obligatorio)</label>
          <select
            className="bini-input"
            value={resMotivo}
            onChange={e => setResMotivo(e.target.value)}
          >
            {MOTIVOS_PERDIDA.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="bini-field" style={{ marginBottom: 12 }}>
          <label>Detalle (opcional)</label>
          <textarea
            className="bini-input"
            rows={2}
            value={resDetalle}
            onChange={e => setResDetalle(e.target.value)}
            placeholder="Ej: Nos pidieron bajar 15% y no daba el margen."
            style={{ resize: "vertical", minHeight: 44 }}
          />
        </div>
      </ConfirmModal>

      {/* modal adjuntar N° OC (post-aprobación) */}
      <ConfirmModal
        open={!!attachOCTarget}
        title="Adjuntar N° OC de cliente"
        description={`Cotización N° ${attachOCTarget?.number || "—"} — ${attachOCTarget?.client?.name || "—"}`}
        confirmLabel="Adjuntar"
        onConfirm={handleAttachOC}
        onCancel={() => { setAttachOCTarget(null); setOcInput(""); }}
      >
        <div className="bini-field" style={{ marginBottom: 12 }}>
          <label>N° OC del cliente</label>
          <input
            className="bini-input bini-mono"
            value={ocInput}
            onChange={e => setOcInput(e.target.value)}
            placeholder="Ej: OC-45242"
            autoFocus
          />
        </div>
      </ConfirmModal>

    </div>
  );
};

export default Quotations;
